import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {AssaysFeature, RecipesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import SharedStudyOverviewTabContents from 'studies/typed/shared/SharedStudyOverviewTabContents';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {
  IsDesignMissingRequiredApprovalStudyNodeFragment,
  isDesignMissingRequiredApprovalForStudyExecution,
} from 'studies/typed/utils/StudyDesignUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import APIUtil from 'util/APIUtil';

import {StudyOverviewViewQuery, StudyOverviewViewQueryVariables} from './__generated/StudyOverviewViewQuery';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;

interface StudyOverviewViewProps {
  id: string;
  // added to support links to runs. Currently only supported for procedure based runs
  runOrPlanId?: string;
}

export const STUDY_OVERVIEW_VIEW_QUERY: DocumentNode = gql`
  query StudyOverviewViewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $id) {
      ... on StudyNode {
        id
        name
        phase
        editUrl
        description
        studySchema {
          id
          studyType
        }
        permissions {
          canChangeStudyableAssignment
          writable
        }
        humanId
        archiveRecord {
          purpose
        }
        studySchema {
          id
          isDesignApprovalRequired
        }
        designEntry {
          ... on EntryNode {
            id
            name
            editUrl
            review {
              ...ReviewSummaryGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureRuns {
          ... on ProcedureRunNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        studyDesign {
          ... on TrialSampleTestingStudyDesignNode {
            id
            treatmentGroups {
              id
            }
            sampleDefinitions {
              id
            }
            sampleTestPlans {
              id
            }
          }
        }
      }
      ...IsDesignMissingRequiredApprovalStudyNodeFragment
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ReviewSummary').fragment}
  ${IsDesignMissingRequiredApprovalStudyNodeFragment.fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function StudyOverview({
  study,
  refetch,
  runId,
  runPlanId,
}: {
  study: StudyNode;
  refetch: () => Promise<unknown>;
  runId?: string;
  runPlanId?: string;
}): JSX.Element {
  const adapter = getTypedStudyAdapter(study.studySchema.studyType);
  const StudyExecutionOverview = adapter.overview.ui.execution;
  const StudyOverViewTabContents = adapter.overview.ui.tab ?? SharedStudyOverviewTabContents;
  const StudyDesignOverview = adapter.overview.ui.design || (() => null);
  const isEditable =
    study.permissions.writable &&
    (study.studySchema.studyType !== StudyType.PROCESS_DEVELOPMENT ||
      isFeatureEnabled({feature: RecipesFeature.RECIPES_EXECUTE_EXPERIMENT})) &&
    (study.studySchema.studyType !== StudyType.ASSAY_DEVELOPMENT ||
      isFeatureEnabled({feature: AssaysFeature.ASSAYS_EXECUTE_EXPERIMENT}));
  return (
    <StudyOverViewTabContents
      refetch={refetch}
      study={study}
      runId={runId}
      runPlanId={runPlanId}
      isEditable={isEditable}
      isDesignApprovalRequiredToProceed={isDesignMissingRequiredApprovalForStudyExecution(study)}
      isAnalysisEnabled={
        BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS && adapter.review.analysis != null
      }
      design={<StudyDesignOverview study={study} />}
      execution={<StudyExecutionOverview study={study} />}
      allowReturnToDesignPhase={adapter.allowReturnToDesignPhase}
    />
  );
}

export default function StudyOverviewView({id, runOrPlanId}: StudyOverviewViewProps): JSX.Element | null {
  const result = useBenchlingQuery<StudyOverviewViewQuery, StudyOverviewViewQueryVariables>(
    STUDY_OVERVIEW_VIEW_QUERY,
    {variables: {id}, renderCachedDataWhenRefetching: true}
  );
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    if (isInaccessible(data.study)) {
      return (
        <WorkspaceItemInaccessibleGuard inaccessibleInfo={data.study} modelType={ModelTypeConstants.STUDY} />
      );
    } else {
      let runId: string | undefined;
      let runPlanId: string | undefined;
      if (runOrPlanId) {
        const fileType = APIUtil.apiIdToMaybeType(runOrPlanId);
        if (fileType === ModelTypeConstants.PROCEDURE_RUN) {
          runId = runOrPlanId;
        } else if (fileType === ModelTypeConstants.PROCEDURE_RUN_PLAN) {
          runPlanId = runOrPlanId;
        }
      }
      return (
        <StudyOverview study={data.study} runId={runId} runPlanId={runPlanId} refetch={result.refetch} />
      );
    }
  } else {
    return null;
  }
}
